import React, { FC, useEffect, useMemo } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import Modal from "@/components/Modal";
import Button from "@/components/Button";
import Select from "@/components/Select";
import { useAppTheme } from "@/utils/theme";
import { useUserStore } from "@/hooks/useUserStore";
import {
  SETTING_APPLICATIONS,
  SETTING_VALUE_TYPES,
  Setting,
  SettingPayload,
  SettingValueType,
} from "@/domain/settingCatalog.interface";
import { parseSettingValue, settingValueToInput } from "@/utils/settingValue";
import settingValidationSchema, { SettingFormData } from "./validation.schema";

type SettingModalProps = {
  open: boolean;
  onClose: () => void;
  setting?: Setting;
  onSave: (data: SettingPayload) => Promise<void>;
  isPending: boolean;
};

const DEFAULT_VALUES: SettingFormData = {
  product: "parca",
  application: "backoffice",
  module: "",
  section: "",
  name: "",
  description: "",
  type: "string",
  defaultValue: "",
  active: "true",
};

const SettingModal: FC<SettingModalProps> = ({
  open,
  onClose,
  setting,
  onSave,
  isPending,
}) => {
  const { t } = useTranslation("common");
  const theme = useAppTheme();
  const { userInfo } = useUserStore();
  const isEdit = !!setting;

  const applicationOptions = useMemo(
    () => SETTING_APPLICATIONS.map((a) => ({ key: a, value: a })),
    []
  );
  const typeOptions = useMemo(
    () => SETTING_VALUE_TYPES.map((ty) => ({ key: ty, value: ty })),
    []
  );

  const methods = useForm<SettingFormData>({
    resolver: yupResolver(settingValidationSchema(t)),
    mode: "all",
    defaultValues: DEFAULT_VALUES,
  });

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = methods;

  const watchedType = (useWatch({ control, name: "type" }) ||
    "string") as SettingValueType;

  useEffect(() => {
    if (setting) {
      reset({
        product: setting.product,
        application: setting.application,
        module: setting.module,
        section: setting.section,
        name: setting.name,
        description: setting.description || "",
        type: setting.type,
        defaultValue: settingValueToInput(setting.type, setting.default),
        active: setting.active ? "true" : "false",
      });
    } else {
      reset(DEFAULT_VALUES);
    }
  }, [setting, reset]);

  const onSubmit = async (data: SettingFormData) => {
    const parsed = parseSettingValue(
      data.type as SettingValueType,
      data.defaultValue ?? ""
    );
    if (!parsed.ok) return; // schema already guards this; bail defensively

    const currentUserId = userInfo?._id as string;
    const payload: SettingPayload = {
      product: data.product.trim(),
      application: data.application as SettingPayload["application"],
      module: data.module.trim(),
      section: data.section.trim(),
      name: data.name.trim(),
      description: (data.description || "").trim(),
      type: data.type as SettingValueType,
      default: parsed.value,
      active: data.active === "true",
      ...(isEdit ? { updatedBy: currentUserId } : { createdBy: currentUserId }),
    };
    await onSave(payload);
  };

  const renderDefaultValueField = () => (
    <Controller
      name="defaultValue"
      control={control}
      render={({ field }) => {
        if (watchedType === "boolean") {
          return (
            <Select
              handleChange={(value) => field.onChange(value)}
              data={[
                { key: "true", value: "true" },
                { key: "false", value: "false" },
              ]}
              value={(field.value as string) || ""}
              label="Default value"
              id="setting-default-boolean"
              required
              error={!!errors.defaultValue}
              errorMessage={errors.defaultValue?.message || ""}
            />
          );
        }

        const isJson = watchedType === "object" || watchedType === "array";
        return (
          <TextField
            {...field}
            id="setting-default"
            label="Default value"
            variant="outlined"
            required
            type={watchedType === "number" ? "number" : "text"}
            value={field.value ?? ""}
            onChange={(e) => field.onChange(e.target.value)}
            error={!!errors.defaultValue}
            helperText={
              errors.defaultValue?.message ||
              (isJson ? "Enter valid JSON" : null)
            }
            multiline={isJson}
            minRows={isJson ? 3 : undefined}
            maxRows={isJson ? 8 : undefined}
            fullWidth
          />
        );
      }}
    />
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      isPending={isPending}
      title={isEdit ? "Edit Setting" : "New setting"}
      theme={theme}
      size={{
        width: "100%",
        minHeight: 560,
        maxHeight: 760,
        maxWidth: 800,
      }}
      content={
        <FormProvider {...methods}>
          <Box sx={{ width: "100%", pt: "12px" }}>
            <Grid container spacing={"20px"}>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="setting-name"
                      label="Name"
                      variant="outlined"
                      required
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      error={!!errors.name}
                      helperText={errors.name ? errors.name.message : null}
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="application"
                  control={control}
                  render={({ field }) => (
                    <Select
                      handleChange={(value) => field.onChange(value)}
                      data={applicationOptions}
                      value={field.value as string}
                      label="Application"
                      id="setting-application"
                      required
                      error={!!errors.application}
                      errorMessage={errors.application?.message || ""}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Controller
                  name="product"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="setting-product"
                      label="Product"
                      variant="outlined"
                      required
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      error={!!errors.product}
                      helperText={
                        errors.product ? errors.product.message : null
                      }
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Controller
                  name="module"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="setting-module"
                      label="Module"
                      variant="outlined"
                      required
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      error={!!errors.module}
                      helperText={errors.module ? errors.module.message : null}
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Controller
                  name="section"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="setting-section"
                      label="Section"
                      variant="outlined"
                      required
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      error={!!errors.section}
                      helperText={
                        errors.section ? errors.section.message : null
                      }
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="type"
                  control={control}
                  render={({ field }) => (
                    <Select
                      handleChange={(value) => {
                        // Clear the default editor when the type changes so a
                        // stale value (e.g. JSON left over from "object") can't
                        // linger under a different type.
                        if (value !== field.value) {
                          setValue("defaultValue", "", {
                            shouldValidate: true,
                          });
                        }
                        field.onChange(value);
                      }}
                      data={typeOptions}
                      value={field.value as string}
                      label="Type"
                      id="setting-type"
                      required
                      error={!!errors.type}
                      errorMessage={errors.type?.message || ""}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="active"
                  control={control}
                  render={({ field }) => (
                    <Select
                      handleChange={(value) => field.onChange(value)}
                      data={[
                        { key: "true", value: "Active" },
                        { key: "false", value: "Inactive" },
                      ]}
                      value={field.value as string}
                      label="Active"
                      id="setting-active"
                      required
                      error={!!errors.active}
                      errorMessage={errors.active?.message || ""}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                {renderDefaultValueField()}
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="setting-description"
                      label="Description"
                      variant="outlined"
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      error={!!errors.description}
                      helperText={
                        errors.description ? errors.description.message : null
                      }
                      multiline
                      minRows={2}
                      maxRows={5}
                      fullWidth
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Box>
        </FormProvider>
      }
      actions={
        <Box sx={{ display: "flex", gap: "10px" }}>
          <Button
            variantType="cancel"
            onClick={onClose}
            disabled={isPending}
            label="CANCEL"
          />
          <Button
            variantType="save"
            onClick={() => handleSubmit(onSubmit)()}
            disabled={isPending}
            isLoading={isPending}
            label="SAVE"
          />
        </Box>
      }
    />
  );
};

export default SettingModal;
